import {
  ApiChartAttribute,
  ApiChartTemplate,
  ApiChartType,
  ApiDimension,
  ApiMetric,
} from "@incendium/api";
import { getPrimaryAnalytics } from "features/analytics/services/analyticsService";
import { buildProductTrendAttributes } from "features/products/utils/productPerformanceFilters";
import { mapTrendRowsToPoints } from "features/products/utils/productUtils";
import { IChart } from "Interfaces";
import { IFromToChartOutput } from "Providers/FromToProvider";

export function buildProductTrendChartConfig(
  productName: string,
  trendMetric: ApiMetric,
  chartAttributes: ApiChartAttribute[] = [],
): IChart {
  return {
    name: "Product Trend",
    dimension: [ApiDimension.DIMENSION_SESSION_START_BY_DAY],
    template: ApiChartTemplate.TREND,
    type: ApiChartType.GRAPH,
    attributes: buildProductTrendAttributes(chartAttributes, [productName]),
    orderBy: `${ApiDimension.DIMENSION_SESSION_START_BY_DAY} ASC`,
    yAxisKeys: [
      {
        key: "l",
        fields: [trendMetric],
      },
    ],
  };
}

export function buildProductTrendCacheKey({
  projectId,
  productName,
  trendMetric,
  chartAttributes,
  chartOutput,
}: {
  projectId?: number;
  productName: string;
  trendMetric: ApiMetric;
  chartAttributes: ApiChartAttribute[];
  chartOutput: IFromToChartOutput | null;
}): string {
  return [
    projectId ?? "",
    productName,
    trendMetric,
    JSON.stringify(chartAttributes),
    chartOutput?.from ? new Date(chartOutput.from).toISOString() : "",
    chartOutput?.to ? new Date(chartOutput.to).toISOString() : "",
    chartOutput?.lastNDays ?? "",
  ].join("|");
}

export async function fetchProductTrendPoints({
  projectId,
  productName,
  trendMetric,
  chartAttributes,
  chartOutput,
}: {
  projectId: number;
  productName: string;
  trendMetric: ApiMetric;
  chartAttributes: ApiChartAttribute[];
  chartOutput: IFromToChartOutput;
}): Promise<number[]> {
  const chart = buildProductTrendChartConfig(
    productName,
    trendMetric,
    chartAttributes,
  );
  const response = await getPrimaryAnalytics(
    projectId,
    chart,
    chartOutput.from,
    chartOutput.to,
    chartOutput.lastNDays,
  );

  return mapTrendRowsToPoints(response.chartData, trendMetric);
}
